import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  useRef,
} from "react";
import { styled } from "@mui/material/styles";
import PropTypes from "prop-types";
import {
  Box,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  Typography,
  useTheme,
} from "@mui/material/";
import searchIcon from "../Icons/icon-mini-search.svg";
import { MapContext } from "../../contexts";
import CloseButton from "../CloseButton";
import common from "../../util/lang/common";
import t__ from "../../util/lang/util";
import cx from "classnames";
import countryFlag from "../Icons/country.svg";
import { countryClicked, resetCountrySelection } from "../Map/mapmethods";
import countryView from "../../util/lang/countryView";
import "../../util/theme/flags.css";
import rawCountryData from "../../data/countries_new.json";
import Skeleton from "@mui/material/Skeleton";
import { visuallyHidden } from "@mui/utils";
import { FootnoteReference } from "../Faqs/faqFootnotes";

export const COUNTRY_HEADLINE_FOOTNOTE_ID = "country-headline-footnote";
export const COUNTRY_HEADLINE_FOOTNOTE_ADDITIONAL_ID =
  "country-headline-footnote-additional";

const PREFIX = "SearchDrawer";

const classes = {
  searchContainer: `${PREFIX}-searchContainer`,
  searchBox: `${PREFIX}-searchBox`,
  focused: `${PREFIX}-focused`,
  searchInput: `${PREFIX}-searchInput`,
  searchInputKeyboardFocus: `${PREFIX}-searchInputKeyboardFocus`,
  backButton: `${PREFIX}-backButton`,
  closex: `${PREFIX}-closex`,
  searchIcon: `${PREFIX}-searchIcon`,
  spyglassButton: `${PREFIX}-spyglassButton`,
  results: `${PREFIX}-results`,
  mapLink: `${PREFIX}-mapLink`,
  resultsContainer: `${PREFIX}-resultsContainer`,
  resultsScrollable: `${PREFIX}-resultsScrollable`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  position: "relative",
  width: "100%",
  pointerEvents: "none",

  [`& .${classes.resultsContainer}`]: {
    pointerEvents: "auto",
    position: "relative",
    zIndex: 2147483601,
    backgroundColor: theme.palette.common.white,
    borderRadius: theme.spacing(3),
    overflow: "hidden",
    boxShadow: theme.shadows[4],
    [theme.breakpoints.up("md")]: {
      borderRadius: theme.spacing(7.5),
    },
  },

  [`& .${classes.resultsScrollable}`]: {
    maxHeight: "550px",
    overflowY: "auto",
    overflowX: "hidden",
  },

  [`& .${classes.searchContainer}`]: {
    overflowX: "hidden",
    width: "100%",
    maxWidth: 900,
    margin: "0 auto",
    background: theme.palette.common.white,
    pointerEvents: "auto",
  },

  [`& .${classes.searchBox}`]: {
    width: "100%",
    height: theme.spacing(15),
    borderColor: theme.palette.borders.light,
    background: theme.palette.common.white,
    borderRadius: theme.spacing(15),
    border: `1px solid ${theme.palette.borders.light}`,
    marginTop: theme.spacing(6),
    position: "relative",
    pointerEvents: "auto",
    [theme.breakpoints.up("md")]: {
      marginTop: theme.spacing(10),
    },
  },

  [`& .${classes.focused}`]: {
    borderColor: theme.palette.primary.main,
  },

  [`& .${classes.searchInput}`]: {
    width: "100%",
    height: theme.spacing(15),
    margin: "0px 1px",
    padding: "0px 58px 0 28px",
    border: "none",
    borderRadius: theme.spacing(15),
    textTransform: "none !important",
    outline: "none",
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.body2.fontSize,
    fontWeight: 500,
    color: theme.palette.text.primary,
    pointerEvents: "auto",
    "&::placeholder": {
      color: theme.palette.text.primary,
    },
    background: "transparent",
    [`&.${classes.searchInputKeyboardFocus}:focus`]: {
      outline: `2px solid ${theme.palette.common.white}`,
      outlineOffset: "2px",
    },
    [theme.breakpoints.down("sm")]: {
      fontSize: theme.typography.body1.fontSize,
    },
  },

  [`& .${classes.closex}`]: {
    position: "absolute",
    right: 60,
    top: "50%",
    width: 34,
    height: 34,
    borderRadius: 34,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: 0,
    "& img": {
      height: 20,
      width: 20,
    },
    "&:focus": {
      backgroundColor: theme.palette.action.selected,
      outline: "none",
    },
    "&:focus-visible": {
      outline: `2px solid ${theme.palette.focus}`,
      outlineOffset: "2px",
      backgroundColor: theme.palette.action.selected,
    },
  },

  [`& .${classes.searchIcon}`]: {
    top: 7,
  },

  [`& .${classes.spyglassButton}`]: {
    position: "absolute",
    right: 3,
    top: "50%",
    transform: "translateY(-50%)",
    marginRight: "2px",
    width: 46,
    height: 46,
    minWidth: 46,
    padding: 0,
    border: "none",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    color: theme.palette.common.white,
    backgroundColor: theme.palette.primary.light,
    transition: "background-color 0.2s ease",
    "&:hover": {
      backgroundColor: "#d45a1f",
    },
    "&:focus": {
      outline: "none",
      backgroundColor: "#d45a1f",
    },
    "&:focus-visible": {
      outline: `2px solid ${theme.palette.focus}`,
      outlineOffset: "2px",
      backgroundColor: "#d45a1f",
    },
    "& img": {
      display: "block",
      height: 20,
      width: 20,
      filter: "brightness(0) invert(1)",
    },
  },

  [`& .${classes.results}`]: {
    "& span": {
      display: "block",
    },
  },

  [`& .${classes.mapLink}`]: {
    display: "block",
    marginBottom: theme.spacing(5),
    color: `${theme.palette.secondary.main} !important`,
  },
}));

const MapSearch = ({ map, zoom, dataReady }) => {
  const theme = useTheme();
  const {
    acceptanceData,
    selectedCountry,
    setSelectedCountry,
    activeTooltip,
    tooltipFocusTargetRef,
  } = useContext(MapContext);
  const [inputFocused, setInputFocused] = useState(false);
  const [textEntered, setTextEntered] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [countryResults, setCountryResults] = useState([]);
  const [countryList, setCountryList] = useState(null);
  const [loader, setLoader] = useState(true);
  const [activeOptionIndex, setActiveOptionIndex] = useState(-1);
  const blurTimeoutRef = useRef(null);
  const inputRef = useRef(null);
  const searchButtonRef = useRef(null);
  const clearButtonRef = useRef(null);
  const searchContainerRef = useRef(null);
  const focusFromKeyboardRef = useRef(false);
  const inputUpdatedByArrowKeysRef = useRef(false);
  const [showInputFocusRing, setShowInputFocusRing] = useState(false);

  const LISTBOX_ID = "search-results-listbox";
  const optionIds = useMemo(
    () => countryResults.map((c) => `search-option-country-${c}`),
    [countryResults]
  );
  const hasResults = optionIds.length > 0;
  const activeOptionId =
    activeOptionIndex >= 0 && activeOptionIndex < optionIds.length
      ? optionIds[activeOptionIndex]
      : null;

  const getAllCountries = useCallback(() => {
    if (!acceptanceData) return [];
    return acceptanceData
      .filter((a) =>
        rawCountryData.features.find((f) => f.properties.adm0_a3 === a.cca3)
      )
      .map((a) => a.cca3)
      .sort((a, b) => {
        const countryA = acceptanceData.find((c) => c.cca3 === a);
        const countryB = acceptanceData.find((c) => c.cca3 === b);
        const nameA = (
          countryA?.translated_name ??
          countryA?.common_name ??
          ""
        ).toLowerCase();
        const nameB = (
          countryB?.translated_name ??
          countryB?.common_name ??
          ""
        ).toLowerCase();
        return nameA.localeCompare(nameB);
      });
  }, [acceptanceData]);

  useEffect(() => {
    setLoader(true);
    setTimeout(function () {
      setLoader(false);
    }, 2000);
  }, []);

  useEffect(() => {
    return () => clearBlurTimeout();
  }, []);

  // Show input focus ring only when user tabbed in (not on click)
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Tab") focusFromKeyboardRef.current = true;
    };
    const onMouseDown = () => {
      focusFromKeyboardRef.current = false;
    };
    document.addEventListener("keydown", onKeyDown, true);
    document.addEventListener("mousedown", onMouseDown, true);
    return () => {
      document.removeEventListener("keydown", onKeyDown, true);
      document.removeEventListener("mousedown", onMouseDown, true);
    };
  }, []);

  useEffect(() => {
    setActiveOptionIndex(-1);
  }, [countryResults, searchQuery]);

  // Scroll the active option into view (APG: essential for zoom and screen reader users)
  useEffect(() => {
    if (activeOptionId) {
      const el = document.getElementById(activeOptionId);
      if (el) {
        el.scrollIntoView({ block: "nearest", behavior: "smooth" });
      }
    }
  }, [activeOptionId]);

  useEffect(() => {
    if (!acceptanceData) {
      return;
    }
    const updatedAcceptance = acceptanceData.filter((a) =>
      rawCountryData.features.find((f) => f.properties.adm0_a3 === a.cca3)
    );

    let baseList = updatedAcceptance.map((a) => {
      const whichname = a.translated_name ?? a.common_name;
      return { name: whichname?.toLowerCase(), code: a.cca3 };
    });

    //a bunch of fun variations that I am shoving in here.
    baseList.push({ name: "u.s. virgin islands", code: "VIR" });
    baseList.push({ name: "us virgin islands", code: "VIR" });
    baseList.push({ name: "virgin islands", code: "VIR" });
    baseList.push({ name: "usvi", code: "VIR" });
    baseList.push({ name: "st thomas", code: "VIR" });
    baseList.push({ name: "st. thomas", code: "VIR" });
    baseList.push({ name: "saint thomas", code: "VIR" });
    setCountryList(baseList);
  }, [acceptanceData]);

  useEffect(() => {
    const trimmed = textEntered.trim();
    if (trimmed === "") {
      setCountryResults(getAllCountries());
      return;
    }
    const countries = [];

    countryList?.forEach((c) => {
      const matchesName = c.name?.substring(0, trimmed.length) === trimmed;
      const matchesCode =
        c.code?.toLowerCase().substring(0, trimmed.length) === trimmed;
      if (matchesName || matchesCode) {
        countries.push(c.code);
      }
    });

    setCountryResults([...new Set(countries)]);
  }, [countryList, textEntered, getAllCountries]);

  // Sync input to selected country when coming from URL/context (do not clear on blur so spyglass close doesn't change the value)
  useEffect(() => {
    if (selectedCountry?.code && acceptanceData) {
      const countryData = acceptanceData.find(
        (a) => a.cca3 === selectedCountry.code
      );
      if (countryData) {
        const countryName =
          countryData.translated_name ?? countryData.common_name;
        setSearchQuery(countryName);
        setTextEntered(countryName.toLowerCase());
      }
    }
  }, [selectedCountry, acceptanceData]);

  const clearBlurTimeout = () => {
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
      blurTimeoutRef.current = null;
    }
  };

  const handleInputFocus = () => {
    clearBlurTimeout();
    if (focusFromKeyboardRef.current) {
      setShowInputFocusRing(true);
      focusFromKeyboardRef.current = false;
    }
    setInputFocused(true);
    // If there's a search query, sync textEntered to filter results
    // If empty, show default (all countries)
    if (searchQuery) {
      setTextEntered(searchQuery.toLowerCase());
    } else {
      setTextEntered("");
    }
    // Scroll to the input when it receives focus (with -20px offset for all viewports)
    if (inputRef.current) {
      setTimeout(() => {
        const el = inputRef.current;
        if (el) {
          const rect = el.getBoundingClientRect();
          const targetY = window.scrollY + rect.top - 20;
          window.scrollTo({ top: Math.max(0, targetY), behavior: "smooth" });
        }
      }, 100);
    }
  };

  const handleInputBlur = (e) => {
    setShowInputFocusRing(false);
    // Use requestAnimationFrame to check focus after the browser has processed the focus change
    requestAnimationFrame(() => {
      // Check if focus is moving to an element within the search container
      const activeElement = document.activeElement;
      if (searchContainerRef.current && activeElement) {
        if (searchContainerRef.current.contains(activeElement)) {
          // Focus is within the search container, keep dropdown open
          clearBlurTimeout();
          return;
        }
      }

      // Use a small delay to check if focus moved to a related element
      // This handles cases where focus changes are asynchronous
      blurTimeoutRef.current = setTimeout(() => {
        const currentActiveElement = document.activeElement;
        if (searchContainerRef.current && currentActiveElement) {
          if (searchContainerRef.current.contains(currentActiveElement)) {
            // Focus is within the search container, keep dropdown open
            clearBlurTimeout();
            return;
          }
        }
        setInputFocused(false);
        blurTimeoutRef.current = null;
      }, 150);
    });
  };

  const handleResultsMouseDown = (e) => {
    e.preventDefault();
    clearBlurTimeout();
  };

  const handleSelection = useCallback((name) => {
    setSearchQuery(name);
    setTextEntered(name.toLowerCase());
    clearBlurTimeout();
    setInputFocused(false);
  }, []);

  const resetInput = () => {
    setSearchQuery("");
    setTextEntered("");
    setSelectedCountry(null);
    // Set dropdown back to default (all countries) list immediately
    setCountryResults(getAllCountries());
    // Reset the map's active country state so tooltip will show when clicking the same country again
    if (map && activeTooltip) {
      resetCountrySelection(map, activeTooltip);
    }
    // Show dropdown with default list and return focus to input (without scrolling page)
    setInputFocused(true);
    if (inputRef.current) {
      inputRef.current.focus({ preventScroll: true });
    }
  };

  const handleSelectOptionById = useCallback(
    (optionId, focusViewDetails = false) => {
      if (!optionId) return;
      if (optionId.startsWith("search-option-country-")) {
        const c = optionId.replace("search-option-country-", "");
        const feature = rawCountryData.features.find(
          (f) => f.properties.adm0_a3 === c
        );
        if (!feature || !acceptanceData) return;
        const found = acceptanceData.find((a) => a.cca3 === c);
        if (!found) return;
        const foundName = found.translated_name ?? found.common_name;
        let clicked = null;
        map.data.forEach((feat) => {
          if (feat.getProperty("adm0_a3") === c) {
            clicked = feat;
          }
        });
        handleSelection(foundName);
        setActiveOptionIndex(-1);
        if (window.scrollY === 0) window.scrollTo(0, 0);
        map.setZoom(zoom);
        setSelectedCountry({
          code: clicked?.getProperty("adm0_a3"),
          region: clicked?.getProperty("region_wb"),
        });
        if (activeTooltip?.current) {
          activeTooltip.current.close();
          activeTooltip.current = null;
        }
        if (clicked) {
          countryClicked(
            map,
            setSelectedCountry,
            activeTooltip,
            theme,
            null,
            clicked,
            tooltipFocusTargetRef
          );
        }
        // Focus clear button or View Details after map/InfoWindow have settled
        setTimeout(() => {
          if (focusViewDetails && tooltipFocusTargetRef?.current) {
            tooltipFocusTargetRef.current.focus({ preventScroll: true });
          } else {
            clearButtonRef.current?.focus({ preventScroll: true });
          }
        }, 150);
      }
    },
    [
      handleSelection,
      map,
      zoom,
      acceptanceData,
      activeTooltip,
      setSelectedCountry,
      theme,
      tooltipFocusTargetRef,
    ]
  );

  const getExactMatchCountryOptionId = useCallback(() => {
    const trimmed = textEntered?.trim();
    if (!trimmed || !countryList) return null;
    const exactMatch = countryList.find(
      (c) => c.name === trimmed || c.code?.toLowerCase() === trimmed
    );
    return exactMatch ? `search-option-country-${exactMatch.code}` : null;
  }, [textEntered, countryList]);

  const getDisplayTextForOptionId = useCallback(
    (optionId) => {
      if (!optionId) return "";
      const code = optionId.replace("search-option-country-", "");
      const found = acceptanceData?.find((a) => a.cca3 === code);
      return found ? (found.translated_name ?? found.common_name) : "";
    },
    [acceptanceData]
  );

  const handleInputKeyDown = (e) => {
    if (e.key === "Escape" && inputFocused) {
      e.preventDefault();
      clearBlurTimeout();
      setActiveOptionIndex(-1);
      setInputFocused(false);
      if (searchButtonRef.current) {
        searchButtonRef.current.focus();
      } else if (inputRef.current) {
        inputRef.current.blur();
      }
      return;
    }
    if (!inputFocused || !hasResults) {
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      inputUpdatedByArrowKeysRef.current = true;
      // APG: wrap from last to first; from none (-1) go to first
      setActiveOptionIndex((i) => (i < optionIds.length - 1 ? i + 1 : 0));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      inputUpdatedByArrowKeysRef.current = true;
      // APG: wrap from first to last; from none (-1) go to last
      setActiveOptionIndex((i) => (i > 0 ? i - 1 : optionIds.length - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      clearBlurTimeout();
      if (activeOptionId) {
        handleSelectOptionById(activeOptionId);
      } else {
        const exactOptionId = getExactMatchCountryOptionId();
        if (exactOptionId) {
          handleSelectOptionById(exactOptionId);
        }
      }
    } else if (
      (e.key === "ArrowLeft" ||
        e.key === "ArrowRight" ||
        e.key === "Home" ||
        e.key === "End") &&
      activeOptionIndex >= 0
    ) {
      // APG: return "visual focus" to textbox so cursor keys edit the input
      setActiveOptionIndex(-1);
      // allow default: input handles cursor movement
    }
  };

  return (
    <Box
      width="100%"
      height={{ xs: "275px", md: "375px" }}
      backgroundColor={theme.palette.secondary.light}
      color="common.white"
      py={{ xs: 10, md: "80px" }}
      px={{ xs: 5, md: 0 }}
      display="flex"
      justifyContent="center"
    >
      <Box
        maxWidth="800px"
        mx="auto"
        textAlign="center"
        data-aos="fade-up"
        data-aos-delay="400"
        data-aos-duration="500"
        data-aos-once="true"
      >
        <Typography
          variant="h1_variant"
          id="country-headline-copy"
          component="h1"
          tabIndex={-1}
          aria-describedby={COUNTRY_HEADLINE_FOOTNOTE_ID}
          sx={{
            outline: "none",
            "&:focus": {
              outline: "none",
              textDecoration: "underline",
              textDecorationColor: theme.palette.common.white,
              textUnderlineOffset: 4,
            },
          }}
        >
          {t__(theme.lang, countryView, "countryHeadline")}
          <FootnoteReference
            marker="*"
            footnoteId={COUNTRY_HEADLINE_FOOTNOTE_ID}
          />
        </Typography>
        <Typography
          variant="h2_variant"
          mt={2}
          component="p"
          fontWeight={400}
        >
          {t__(theme.lang, countryView, "countryHeadlineCopy")}
          <FootnoteReference
            marker="1"
            footnoteId={COUNTRY_HEADLINE_FOOTNOTE_ADDITIONAL_ID}
            sx={{ fontSize: "0.6em" }}
          />
        </Typography>

        {(dataReady ?? Boolean(acceptanceData)) ? (
          <StyledBox
            ref={searchContainerRef}
            className={classes.searchContainer}
            role="search"
          >
            <Box display="flex">
              <Box
                className={cx(
                  classes.searchBox,
                  inputFocused && classes.focused
                )}
                bgcolor="transparent"
              >
                <input
                  ref={inputRef}
                  id="country-search-input"
                  type="text"
                  placeholder="Enter a country or territory"
                  className={cx(
                    classes.searchInput,
                    showInputFocusRing && classes.searchInputKeyboardFocus
                  )}
                  autoComplete="off"
                  role="combobox"
                  aria-expanded={inputFocused && hasResults}
                  aria-controls={hasResults ? LISTBOX_ID : undefined}
                  aria-activedescendant={activeOptionId || undefined}
                  aria-autocomplete="list"
                  onFocus={handleInputFocus}
                  onBlur={handleInputBlur}
                  onKeyDown={handleInputKeyDown}
                  onChange={(e) => {
                    const value = e.target.value;
                    setSearchQuery(value);
                    setTextEntered(value.toLowerCase());
                  }}
                  value={
                    activeOptionIndex >= 0 &&
                    activeOptionId &&
                    inputUpdatedByArrowKeysRef.current
                      ? getDisplayTextForOptionId(activeOptionId)
                      : searchQuery
                  }
                  aria-label={
                    searchQuery
                      ? `Selected ${searchQuery}`
                      : "Enter a country or territory"
                  }
                  aria-describedby="searchInfo"
                />
                <Typography style={visuallyHidden} id="searchInfo">
                  Use the up and down arrow keys to navigate results. Press
                  Enter to select.
                </Typography>
                {inputFocused && searchQuery && countryResults.length === 0 && (
                  <span
                    style={visuallyHidden}
                    role="status"
                    aria-live="assertive"
                    aria-atomic="true"
                  >
                    {`${t__(theme.lang, common, "noMatch")} "${searchQuery}". ${t__(theme.lang, common, "noMatchCopy")}`}
                  </span>
                )}
                {searchQuery && (
                  <CloseButton
                    ref={clearButtonRef}
                    alt={`Selected ${searchQuery}, clear`}
                    className={classes.closex}
                    onClick={(e) => {
                      e.preventDefault();
                      resetInput();
                    }}
                    sx={{
                      transform: "translateY(-50%)",
                      transition: "right 0.2s ease",
                    }}
                  />
                )}
                <button
                  ref={searchButtonRef}
                  type="button"
                  className={classes.spyglassButton}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    clearBlurTimeout();
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && searchQuery) {
                      const exactOptionId = getExactMatchCountryOptionId();
                      if (exactOptionId) {
                        e.preventDefault();
                        handleSelectOptionById(exactOptionId, true);
                        setInputFocused(false);
                        setActiveOptionIndex(-1);
                      } else {
                        e.preventDefault();
                        setInputFocused(true);
                        inputRef.current?.focus({ preventScroll: true });
                      }
                      return;
                    }
                    if (e.key === "Tab") {
                      if (inputFocused) {
                        setInputFocused(false);
                        if (inputRef.current) inputRef.current.blur();
                      }
                      if (!e.shiftKey && tooltipFocusTargetRef?.current) {
                        e.preventDefault();
                        tooltipFocusTargetRef.current.focus();
                      }
                    }
                  }}
                  onClick={() => {
                    if (searchQuery) {
                      const exactOptionId = getExactMatchCountryOptionId();
                      if (exactOptionId) {
                        handleSelectOptionById(exactOptionId, true);
                        setInputFocused(false);
                        setActiveOptionIndex(-1);
                        return;
                      }
                      inputRef.current?.focus({ preventScroll: true });
                      return;
                    }
                    if (inputFocused) {
                      const exactOptionId = getExactMatchCountryOptionId();
                      if (exactOptionId) {
                        handleSelectOptionById(exactOptionId);
                        return;
                      }
                      setInputFocused(false);
                      if (inputRef.current) {
                        inputRef.current.blur();
                      }
                      if (searchButtonRef.current) {
                        searchButtonRef.current.focus();
                      }
                    } else {
                      setInputFocused(true);
                      if (inputRef.current) {
                        inputRef.current.focus();
                      }
                    }
                  }}
                  aria-label={t__(theme.lang, common, "search")}
                >
                  <img
                    src={searchIcon}
                    alt={t__(theme.lang, common, "search")}
                    height={20}
                    width={20}
                  />
                </button>
              </Box>
            </Box>

            {inputFocused && (
              <Box className={classes.resultsContainer} marginTop={4}>
                <Box
                  id={LISTBOX_ID}
                  role="listbox"
                  aria-label={t__(theme.lang, common, "search")}
                  className={classes.resultsScrollable}
                  style={{ outline: "none" }}
                  onMouseDown={handleResultsMouseDown}
                  onBlur={(e) => {
                    // Use requestAnimationFrame to check focus after the browser has processed the focus change
                    requestAnimationFrame(() => {
                      const activeElement = document.activeElement;
                      if (searchContainerRef.current && activeElement) {
                        if (
                          searchContainerRef.current.contains(activeElement)
                        ) {
                          // Focus is within the search container, keep dropdown open
                          clearBlurTimeout();
                          return;
                        }
                      }

                      // Use a small delay to check if focus moved to a related element
                      // This handles cases where focus changes are asynchronous
                      blurTimeoutRef.current = setTimeout(() => {
                        const currentActiveElement = document.activeElement;
                        if (
                          searchContainerRef.current &&
                          currentActiveElement
                        ) {
                          if (
                            searchContainerRef.current.contains(
                              currentActiveElement
                            )
                          ) {
                            // Focus is within the search container, keep dropdown open
                            clearBlurTimeout();
                            return;
                          }
                        }
                        setInputFocused(false);
                        blurTimeoutRef.current = null;
                      }, 150);
                    });
                  }}
                >
                  {countryResults.length === 0 ? (
                    <Box paddingY={5} paddingX={8}>
                      <Typography
                        variant="body2"
                        component="p"
                        color={theme.palette.text.primary}
                        align="left"
                      >
                        {`${t__(theme.lang, common, "noMatch")} "${searchQuery}". ${t__(theme.lang, common, "noMatchCopy")}`}
                      </Typography>
                    </Box>
                  ) : (
                    <>
                      {acceptanceData && countryResults.length !== 0 && (
                        <Box
                          marginTop={5}
                          //marginBottom={7}
                          //borderBottom={1}
                          //paddingBottom={7}
                          style={{ borderColor: theme.palette.borders.light }}
                        >
                          {searchQuery && (
                            <>
                              {loader ? (
                                <Box sx={{ paddingX: 7 }}>
                                  <Skeleton
                                    animation="wave"
                                    height={theme.spacing(10)}
                                  />
                                </Box>
                              ) : (
                                <Typography
                                  variant="h5"
                                  color={theme.palette.text.primary}
                                  align="left"
                                  paddingLeft={9}
                                >
                                  <b>
                                    {t__(theme.lang, common, "countriesCopy")}
                                  </b>
                                </Typography>
                              )}
                            </>
                          )}
                          <List className={classes.results}>
                            {countryResults.map((c) => {
                              const feature = rawCountryData.features.find(
                                (f) => f.properties.adm0_a3 === c
                              );
                              if (feature) {
                                const found = acceptanceData.find(
                                  (a) => a.cca3 === c
                                );
                                const foundName =
                                  found.translated_name ?? found.common_name;
                                const optionId = `search-option-country-${c}`;
                                const optionIndex = optionIds.indexOf(optionId);
                                return (
                                  <ListItem
                                    component="li"
                                    role="option"
                                    id={optionId}
                                    aria-selected={
                                      activeOptionIndex === optionIndex
                                    }
                                    disableGutters
                                    key={c}
                                    sx={{
                                      paddingTop: 2,
                                      paddingBottom: 2,
                                      paddingX: 7,
                                      cursor: "pointer",
                                      display: "flex",
                                      alignItems: "center",
                                      backgroundColor:
                                        activeOptionIndex === optionIndex
                                          ? theme.palette.background
                                              .medLightDarker
                                          : "transparent",
                                      "&:hover": {
                                        backgroundColor:
                                          theme.palette.background.medLight,
                                      },
                                    }}
                                    onMouseEnter={() => {
                                      inputUpdatedByArrowKeysRef.current = false;
                                      setActiveOptionIndex(optionIndex);
                                    }}
                                    onMouseDown={(e) => {
                                      e.preventDefault();
                                      clearBlurTimeout();
                                    }}
                                    onClick={() =>
                                      handleSelectOptionById(optionId)
                                    }
                                  >
                                    <ListItemIcon>
                                      {loader ? (
                                        <Skeleton
                                          variant="circular"
                                          width={40}
                                          height={40}
                                        />
                                      ) : (
                                        <Box
                                          width={theme.spacing(10)}
                                          height={theme.spacing(10)}
                                          borderRadius={theme.spacing(10)}
                                          bgcolor={
                                            theme.palette.background.light
                                          }
                                          display="flex"
                                          justifyContent="center"
                                          alignItems="center"
                                          marginLeft={theme.spacing(2)}
                                        >
                                          {feature && feature.properties ? (
                                            <Box
                                              className={`flag:${feature.properties["iso_a2"]}`}
                                              style={{ marginRight: 0 }}
                                            />
                                          ) : (
                                            <img src={countryFlag} alt="flag" />
                                          )}
                                        </Box>
                                      )}
                                    </ListItemIcon>
                                    {loader ? (
                                      <Skeleton
                                        animation="wave"
                                        width="100%"
                                        height={40}
                                      />
                                    ) : (
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          paddingLeft: 2,
                                          color: theme.palette.primary.main,
                                        }}
                                      >
                                        <b>{foundName}</b>
                                      </Typography>
                                    )}
                                  </ListItem>
                                );
                              } else {
                                return null;
                              }
                            })}
                          </List>
                        </Box>
                      )}
                    </>
                  )}
                </Box>
              </Box>
            )}
          </StyledBox>
        ) : (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight={theme.spacing(20)}
            mt={4}
            data-testid="map-search-loading"
          >
            <CircularProgress
              size={40}
              sx={{ color: "common.white" }}
              aria-label="Loading..."
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

MapSearch.propTypes = {
  map: PropTypes.object,
  zoom: PropTypes.number,
  dataReady: PropTypes.bool,
};

export default MapSearch;
